import { Component } from "@theme/component";

/**
 * @typedef {any} Swiper
 */

/**
 * Swiper 组件 - 基于 Swiper.js 的轮播图组件
 *
 * @extends {Component}
 */
class SwiperComponent extends Component {
  static get observedAttributes() {
    return ["open", "lazy"];
  }
   get isMobile() {
    return window.innerWidth < 768;
  }

  /** @type {{ total: number; current: number }} */
  custom_pagination = {
    total: 0,
    current: 0,
  };

  /** @type {any} */
  swiper_options = {
    slidesPerView: 1,
    spaceBetween: 40,
    loop: false,
    autoplay: false,
    watchOverflow: true,
    speed: 500,
  };

  /** @type {Swiper | null} */
  swiper = null;

  /** @type {boolean} */
  lazyInit = false;

  /**
   * 组件初始化
   */
  constructor() {
    super();
    this.lock = true;
    this.custom_prev_btn = this.querySelector(".swiper-pagination-prev");
    this.custom_next_btn = this.querySelector(".swiper-pagination-next");
    this.custom_swiper_tabs = this.querySelectorAll(".swiper-tab-item");
    this.lazyInit = this.hasAttribute("lazy");
  }

  /**
   * 组件从 DOM 中移除时调用
   */
  disconnectedCallback() {
    if (this.swiper) {
      this.swiper.destroy(true, true);
    }
  }

  /**
   * 组件连接到 DOM 时调用
   */
  connectedCallback() {
    this.lazyInit = this.hasAttribute("lazy") || this.lazyInit;

    if (this.lazyInit && !this.hasAttribute("open")) {
      return;
    }

    const targetDevice = this.dataset.targetDevice || "all";
    if (targetDevice == 'mobile' && !this.isMobile) {
      return;
    }
    if (targetDevice == 'desktop' && this.isMobile) {
      return;
    }
    this.startSwiper();
  }

  rebind() {
    this.custom_swiper_tabs = this.querySelectorAll(".swiper-tab-item");
    this.initCuPaginationButton();
  }

  /**
   * 属性变化时调用
   * @param {string} name - 属性名
   * @param {string} oldValue - 旧值
   * @param {string} newValue - 新值
   */
  attributeChangedCallback(name, oldValue, newValue) {
    if (name === "lazy") {
      this.lazyInit = newValue !== null;

      if (!this.lazyInit && this.lock) {
        this.startSwiper();
      }
    }

    if (name === "open" && this.hasAttribute("open") && this.lock) {
      this.lock = false;
      this.startSwiper();
    }
  }

  /**
   * 启动 Swiper
   */
  startSwiper() {
    if (this.swiper) {
      this.swiper.update();
      this.swiper.updateSize();
      this.swiper.updateSlides();
      this.lock = false;
      return;
    }

    this.lock = false;
    this.initPagination();
    this.initSwiper();
    this.initCuPaginationButton();

    // 如果启用了鼠标滚轮释放边缘功能
    if (
      this.swiper_options.mousewheel &&
      this.swiper_options.mousewheel.releaseOnEdges
    ) {
      this.releaseOnEdges();
      this.releaseOnEdgesMobile();
    }
  }

  /**
   * 初始化 Swiper 实例
   */
  initSwiper() {
    // 从 dataset 中读取配置
    Object.keys(this.dataset).forEach((key) => {
      try {
        const value = this.dataset[key];
        if (value) {
          // 处理布尔值字符串
          if (value === "true") {
            this.swiper_options[key] = true;
          } else if (value === "false") {
            this.swiper_options[key] = false;
          } else {
            // 尝试解析 JSON
            try {
              this.swiper_options[key] = JSON.parse(value);
            } catch {
              // 如果解析失败，使用原始字符串值
              this.swiper_options[key] = value;
            }
          }
        }
      } catch (e) {
        console.warn(`Failed to parse dataset.${key}:`, e);
      }
    });

    const swiperElement = this.querySelector(".swiper");
    if (!swiperElement) {
      console.error("Swiper element not found");
      return;
    }

    // 创建 Swiper 实例
    const lazyConfig = this.lazyInit
      ? {
          preloadImages:
            typeof this.swiper_options.preloadImages === "undefined"
              ? false
              : this.swiper_options.preloadImages,
          lazy:
            typeof this.swiper_options.lazy === "undefined"
              ? { enabled: true, loadOnTransitionStart: true }
              : this.swiper_options.lazy,
        }
      : {};

    const config = {
      ...this.swiper_options,
      ...lazyConfig,
      touchStartPreventDefault: false,
      passiveListeners: true,
      // 防止滑动时触发点击事件
      preventClicks: true,
      preventClicksPropagation: true,
    };
    // @ts-ignore - Swiper 是全局变量
    this.swiper = new Swiper(swiperElement, config);

    // 绑定自定义分页
    this.swiper.custom_pagination = this.custom_pagination;

    // 监听幻灯片切换事件
    this.swiper.on("slideChange", (/** @type {any} */ swiper) => {
      this.custom_pagination.current = swiper.realIndex + 1;
      this.swiper.custom_pagination = this.custom_pagination;
      this.updateCuPaginationButton();
      this.selectTabsItem(swiper.realIndex);
    });
  }

  /**
   * 跳转到指定幻灯片
   * @param {number} index - 幻灯片索引
   */
  slideTo(index) {
    if (this.swiper) {
      this.swiper.slideTo(index);
    }
  }

  /**
   * 初始化分页器
   */
  initPagination() {
    // 标准导航按钮
    const nextBtn = this.querySelector(".swiper-button-next");
    const prevBtn = this.querySelector(".swiper-button-prev");
    if (nextBtn && prevBtn) {
      this.swiper_options.navigation = {
        nextEl: nextBtn,
        prevEl: prevBtn,
      };
    }

    // 标准分页器
    const pagination = this.querySelector(".swiper-pagination");
    if (pagination) {
      const isDynamicBullets = pagination.classList.contains(
        "swiper-pagination-dynamicBullets"
      );
      this.swiper_options.pagination = {
        el: pagination,
        clickable: true,
        ...(isDynamicBullets && { dynamicBullets: true }),
      };
    }

    // 自定义分页器
    const customPagination = this.querySelector(".swiper-pagination-custom");
    if (customPagination) {
      this.swiper_options.pagination = {
        el: customPagination,
        clickable: true,
        type: "custom",
        renderCustom: (
          /** @type {any} */ swiper,
          /** @type {number} */ current,
          /** @type {number} */ total
        ) => {
          this.custom_pagination = {
            current,
            total,
          };
          return null;
        },
      };
    }
  }

  /**
   * 初始化自定义分页按钮
   */
  initCuPaginationButton() {
    // 自定义上一页/下一页按钮
    if (this.custom_next_btn && this.custom_prev_btn) {
      this.updateCuPaginationButton();

      if (this.custom_prev_btn instanceof HTMLElement) {
        this.custom_prev_btn.onclick = () => {
          if (this.swiper) {
            this.swiper.slidePrev();
            this.updateCuPaginationButton();
          }
        };
      }

      if (this.custom_next_btn instanceof HTMLElement) {
        this.custom_next_btn.onclick = () => {
          if (this.swiper) {
            this.swiper.slideNext();
            this.updateCuPaginationButton();
          }
        };
      }
    }

    // 自定义标签页
    if (this.custom_swiper_tabs.length > 0) {
      this.selectTabsItem(0);
      this.custom_swiper_tabs.forEach((item, index) => {
        if (item instanceof HTMLElement) {
          item.onclick = () => this.selectTabsItem(index);
        }
      });
    }
  }

  /**
   * 选择标签页项
   * @param {number} index - 标签页索引
   */
  selectTabsItem(index) {
    if (!this.custom_swiper_tabs.length || !this.swiper) {
      return;
    }

    // 移除所有活动状态
    this.custom_swiper_tabs.forEach((tab) => {
      tab.classList.remove("active");
    });

    // 添加当前活动状态
    if (this.custom_swiper_tabs[index]) {
      this.custom_swiper_tabs[index].classList.add("active");
      this.swiper.slideTo(index);
    }
  }

  /**
   * 更新自定义分页按钮状态
   */
  updateCuPaginationButton() {
    if (!this.custom_next_btn || !this.custom_prev_btn || !this.swiper) {
      return;
    }

    if (
      this.custom_prev_btn instanceof HTMLButtonElement ||
      this.custom_prev_btn instanceof HTMLInputElement
    ) {
      this.custom_prev_btn.disabled = this.swiper.activeIndex === 0;
    }

    if (
      this.custom_next_btn instanceof HTMLButtonElement ||
      this.custom_next_btn instanceof HTMLInputElement
    ) {
      this.custom_next_btn.disabled = this.swiper.isEnd;
    }
  }

  /**
   * 在边缘释放鼠标滚轮事件（桌面端）
   */
  releaseOnEdges() {
    const swiperElement = this.querySelector(".swiper");
    if (!swiperElement) return;

    swiperElement.addEventListener(
      "wheel",
      (/** @type {WheelEvent} */ e) => {
        if (!this.swiper) return;

        const delta = e.deltaY;

        // 如果正在动画中，阻止默认行为
        if (this.swiper.animating) {
          e.preventDefault();
          return;
        }

        // 在边缘时允许滚动
        if (
          (this.swiper.isEnd && delta > 0) ||
          (this.swiper.isBeginning && delta < 0)
        ) {
          // 允许页面滚动
        }
      },
      {
        passive: false,
      }
    );
  }

  /**
   * 在边缘释放触摸事件（移动端）
   */
  releaseOnEdgesMobile() {
    const swiperElement = this.querySelector(".swiper");
    if (!swiperElement) return;

    let startY = 0;
    let lock = false;
    const SWIPE_THRESHOLD = 30;

    swiperElement.addEventListener(
      "touchstart",
      (/** @type {TouchEvent} */ e) => {
        if (e.touches[0]) {
          startY = e.touches[0].clientY;
        }
      },
      {
        passive: true,
      }
    );

    swiperElement.addEventListener(
      "touchend",
      (/** @type {TouchEvent} */ e) => {
        if (!this.swiper || lock) return;

        if (!e.changedTouches[0]) return;

        const delta = startY - e.changedTouches[0].clientY;
        const absDelta = Math.abs(delta);

        // 如果滑动距离太小，忽略
        if (absDelta < SWIPE_THRESHOLD) {
          return;
        }

        // 向下滑动
        if (delta > 0) {
          if (this.swiper.isEnd) {
            // 在末尾，允许页面滚动
            window.scrollBy({
              top: delta,
              behavior: "smooth",
            });
          } else {
            // 不在末尾，切换到下一张
            lock = true;
            this.swiper.slideNext();
            this.swiper.once("transitionEnd", () => {
              lock = false;
            });
          }
        } else {
          // 向上滑动
          if (this.swiper.isBeginning) {
            // 在开头，允许页面滚动
            window.scrollBy({
              top: delta,
              behavior: "smooth",
            });
          } else {
            // 不在开头，切换到上一张
            lock = true;
            this.swiper.slidePrev();
            this.swiper.once("transitionEnd", () => {
              lock = false;
            });
          }
        }
      },
      {
        passive: true,
      }
    );
  }
}
// 注册自定义元素
if (!customElements.get("swiper-component")) {
  customElements.define("swiper-component", SwiperComponent);
}


class AccordionComponent extends HTMLElement {
  constructor() {
    super();
  }
  connectedCallback() {
    this.initAccordion();
  }
  initAccordion() {
    const accordionTitle = this.querySelector(".accordion-title");
    const accordionContent = this.querySelector(".accordion-content");
    if (accordionTitle && accordionContent) {
      accordionTitle.addEventListener("click", () => {
        accordionContent.classList.toggle("active");
        accordionTitle.classList.toggle("active");
      });
    }
  }
}
if (!customElements.get("accordion-component")) {
  customElements.define("accordion-component", AccordionComponent);
}

/**
 * 图片懒加载组件
 * 使用 IntersectionObserver API 实现高效的图片懒加载
 *
 * @extends {Component}
 */
class ImgLazyLoad extends Component {
  /** @type {HTMLImageElement[]} */
  images = [];

  /** @type {IntersectionObserver | null} */
  observer = null;

  /** @type {number} */
  preloadScreens = 2.5;

  /** @type {number} */
  preloadOffset = 0;

  /** @type {boolean} */
  isActive = false;

  /** @type {number | null} */
  scrollTimeout = null;

  /**
   * 组件连接到 DOM 时调用
   */
  connectedCallback() {
    super.connectedCallback();
    this.initImageLazyLoad();
  }

  /**
   * 组件从 DOM 中移除时调用
   */
  disconnectedCallback() {
    this.cleanup();
  }

  /**
   * 初始化图片懒加载
   */
  initImageLazyLoad() {
    // this.images = Array.from(this.querySelectorAll("img[data-src]"));
    this.images = Array.from(this.querySelectorAll("img.img-lazyload"));
    
    if (this.images.length === 0) {
      return;
    }

    // 计算预加载偏移量
    this.preloadOffset = window.innerHeight * this.preloadScreens;

    if ("IntersectionObserver" in window) {
      this.initIntersectionObserver();
    } else {
      this.initFallbackLazyLoad();
    }
  }

  /**
   * 使用 IntersectionObserver 初始化懒加载
   */
  initIntersectionObserver() {
    // 计算 rootMargin，使用像素值而不是百分比
    const rootMargin = `${this.preloadOffset}px`;

    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && entry.target instanceof HTMLImageElement) {
            this.loadImage(entry.target);
            if (this.observer) {
              this.observer.unobserve(entry.target);
            }
          }
        });
      },
      {
        rootMargin,
        threshold: 0.01,
      }
    );

    this.images.forEach((img) => {
      if (this.observer) {
        this.observer.observe(img);
      }
    });
  }

  /**
   * 降级方案：使用滚动事件实现懒加载
   */
  initFallbackLazyLoad() {
    const lazyLoad = () => {
      if (this.isActive) return;
      
      this.isActive = true;
      
      // 使用 requestAnimationFrame 优化性能
      requestAnimationFrame(() => {
        /** @type {HTMLImageElement[]} */
        const remainingImages = [];
        
        this.images.forEach((lazyImage) => {
          if (!lazyImage.dataset.src) {
            return; // 已加载的图片
          }

          const rect = lazyImage.getBoundingClientRect();
          const isVisible =
            rect.top <= window.innerHeight + this.preloadOffset &&
            rect.bottom >= -this.preloadOffset &&
            getComputedStyle(lazyImage).display !== "none";

          if (isVisible) {
            this.loadImage(lazyImage);
          } else {
            remainingImages.push(lazyImage);
          }
        });

        this.images = remainingImages;
        this.isActive = false;

        // 如果所有图片都已加载，移除事件监听器
        if (this.images.length === 0) {
          this.removeEventListeners();
        }
      });
    };

    // 节流处理滚动事件
    const throttledLazyLoad = () => {
      if (this.scrollTimeout) {
        cancelAnimationFrame(this.scrollTimeout);
      }
      this.scrollTimeout = requestAnimationFrame(lazyLoad);
    };

    document.addEventListener("scroll", throttledLazyLoad, { passive: true });
    window.addEventListener("resize", throttledLazyLoad, { passive: true });
    window.addEventListener("orientationchange", throttledLazyLoad, {
      passive: true,
    });

    // 保存事件处理器以便后续清理
    this.lazyLoadHandler = throttledLazyLoad;

    // 初始加载一次
    lazyLoad();
  }

  /**
   * 加载图片
   * @param {HTMLImageElement} img - 要加载的图片元素
   */
  loadImage(img) {
    if (!img.dataset.src) {
      return; // 已经加载过
    }

    // 添加加载中状态类
    img.classList.add("lazyloading");

    // 加载图片
    const src = img.dataset.src;
    const srcset = img.dataset.srcset;
    const sizes = img.dataset.sizes;

    // 设置图片源
    if (srcset) {
      img.srcset = srcset;
    }
    if (sizes) {
      img.sizes = sizes;
    }
    img.src = src;

    // 图片加载完成
    img.addEventListener(
      "load",
      () => {
        img.classList.remove("lazyloading");
        img.classList.add("lazyloaded");
        // 移除 data-src 属性以节省内存
        delete img.dataset.src;
        if (img.dataset.srcset) {
          delete img.dataset.srcset;
        }
        if (img.dataset.sizes) {
          delete img.dataset.sizes;
        }
      },
      { once: true }
    );

    // 图片加载错误处理
    img.addEventListener(
      "error",
      () => {
        img.classList.remove("lazyloading");
        img.classList.add("lazyload-error");
        console.warn("图片加载失败:", src);
      },
      { once: true }
    );
  }

  /**
   * 移除事件监听器（降级方案使用）
   */
  removeEventListeners() {
    if (this.lazyLoadHandler) {
      document.removeEventListener("scroll", this.lazyLoadHandler);
      window.removeEventListener("resize", this.lazyLoadHandler);
      window.removeEventListener("orientationchange", this.lazyLoadHandler);
      this.lazyLoadHandler = null;
    }
  }

  /**
   * 清理资源
   */
  cleanup() {
    // 断开 IntersectionObserver
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }

    // 移除事件监听器
    this.removeEventListeners();

    // 清理定时器
    if (this.scrollTimeout) {
      cancelAnimationFrame(this.scrollTimeout);
      this.scrollTimeout = null;
    }
  }
}
if (!customElements.get("img-lazy-load")) {
  customElements.define("img-lazy-load", ImgLazyLoad);
}
/**
 * 数字跟踪器
 * @param {HTMLElement} element - 数字元素
 * @returns {any}
 */
function DigitTracker(element) {
  this.el = element;

  let el = element; // 保存引用以避免类型错误
  let top = el.querySelector('.card__top'),
    bottom = el.querySelector('.card__bottom'),
    back = el.querySelector('.card__back'),
    backBottom = el.querySelector('.card__back .card__bottom');

  this.update = function (/** @type {any} */ val) {
    val = val.toString();
    if (val !== this.currentValue) {
      if (this.currentValue !== undefined && this.currentValue !== null && this.currentValue !== undefined && this.currentValue >= 0) {
        back?.setAttribute('data-value', this.currentValue);
        bottom?.setAttribute('data-value', this.currentValue);
        // 添加 flip 类触发翻转动画
        el?.classList.add('flip');
        // 动画结束后移除 flip 类（动画时长 0.6 秒）
        setTimeout(() => {
          el?.classList.remove('flip');
        }, 600);
      }
      this.currentValue = val;
      top?.setAttribute('data-value', this.currentValue);
      backBottom?.setAttribute('data-value', this.currentValue);
    }
  };
}

/**
 * 全局倒计时管理器
 * 使用单个 RAF 循环统一管理所有倒计时组件，每秒更新一次
 */
class CountdownManager {
  /** @type {Set<any>} */
  #components = new Set();

  /** @type {number | null} */
  #animationFrameId = null;

  /** @type {number | null} */
  #lastUpdateTime = null;

  /**
   * 注册倒计时组件
   * @param {any} component - 倒计时组件实例
   */
  register(component) {
    this.#components.add(component);
    // 如果这是第一个组件，启动 RAF 循环
    if (this.#components.size === 1) {
      this.#start();
    }
  }

  /**
   * 取消注册倒计时组件
   * @param {any} component - 倒计时组件实例
   */
  unregister(component) {
    this.#components.delete(component);
    // 如果没有组件了，停止 RAF 循环
    if (this.#components.size === 0) {
      this.#stop();
    }
  }

  /**
   * 启动 RAF 循环
   */
  #start() {
    if (this.#animationFrameId !== null) {
      return; // 已经启动
    }
    this.#lastUpdateTime = null;
    this.#animationFrameId = requestAnimationFrame((timestamp) => this.#update(timestamp));
  }

  /**
   * 停止 RAF 循环
   */
  #stop() {
    if (this.#animationFrameId !== null) {
      cancelAnimationFrame(this.#animationFrameId);
      this.#animationFrameId = null;
    }
    this.#lastUpdateTime = null;
  }

  /**
   * RAF 更新循环
   * @param {number} timestamp - requestAnimationFrame 提供的时间戳
   */
  #update(timestamp) {
    // 继续请求下一帧
    this.#animationFrameId = requestAnimationFrame((ts) => this.#update(ts));

    // 初始化上次更新时间
    if (this.#lastUpdateTime === null) {
      this.#lastUpdateTime = timestamp;
      return; // 第一次调用，只初始化时间，不更新
    }

    // 检查是否已经过去了 1000 毫秒（1秒）
    const elapsed = timestamp - this.#lastUpdateTime;
    if (elapsed < 1000) {
      return; // 还没到1秒，不更新
    }

    // 更新上次更新时间
    this.#lastUpdateTime = timestamp;

    // 更新所有注册的组件
    this.#components.forEach((component) => {
      if (component && typeof component.update === 'function') {
        try {
          component.update();
        } catch (error) {
          console.error('倒计时组件更新错误:', error);
        }
      }
    });
  }
}

// 创建全局单例
const globalCountdownManager = new CountdownManager();

/**
 * 倒计时跟踪器
 * @param {HTMLElement} tensElement - 十位数字元素
 * @param {HTMLElement} onesElement - 个位数字元素
 * @param {string} label - 标签
 * @returns {any}
 */
function CountdownTracker(tensElement, onesElement, label) {
  this.tensTracker = new DigitTracker(tensElement);
  this.onesTracker = new DigitTracker(onesElement);
  this.label = label;

  this.update = function (/** @type {any} */ val) {
    val = Math.max(0, Math.min(99, val)); // 限制在0-99之间
    let tensDigit = Math.floor(val / 10);
    let onesDigit = val % 10;

    this.tensTracker.update(tensDigit);
    this.onesTracker.update(onesDigit);
  };
}

class FlipCountdownComponent extends Component {

  /** @type {NodeListOf<HTMLElement> | null} */
  #countdownPieces = null

  /** @type { any} */
  #countdownTrackers = null

  /** @type {boolean} */
  isCountdownFinished = false;

  /** @type {number | null} */
  endTimeTimestamp = null;


  get countdownDate() {
    const countdownDate = this.getAttribute("countdown-date");
    if (!countdownDate) {
      return null;
    }
    return new Date(countdownDate);
  }
  connectedCallback() {
    super.connectedCallback();
    this.#countdownPieces = this.querySelectorAll(".flip-clock__piece");
    this.#countdownTrackers = {
      Days: new CountdownTracker(
        // @ts-ignore
        this.#countdownPieces[0].querySelectorAll(".flip-clock__digit")[0].querySelector(".card") ?? null,
        // @ts-ignore
        this.#countdownPieces[0].querySelectorAll(".flip-clock__digit")[1].querySelector(".card"),
        "Days"
      ),
      Hours: new CountdownTracker(
        // @ts-ignore
        this.#countdownPieces[1].querySelectorAll(".flip-clock__digit")[0].querySelector(".card") ?? null,
        // @ts-ignore
        this.#countdownPieces[1].querySelectorAll(".flip-clock__digit")[1].querySelector(".card") ?? null,
        "Hours"
      ),
      Minutes: new CountdownTracker(
        // @ts-ignore
        this.#countdownPieces[2].querySelectorAll(".flip-clock__digit")[0].querySelector(".card") ?? null,
        // @ts-ignore
        this.#countdownPieces[2].querySelectorAll(".flip-clock__digit")[1].querySelector(".card") ?? null,
        "Minutes"
      ),
      Seconds: new CountdownTracker(
        // @ts-ignore
        this.#countdownPieces[3].querySelectorAll(".flip-clock__digit")[0].querySelector(".card") ?? null,
        // @ts-ignore
        this.#countdownPieces[3].querySelectorAll(".flip-clock__digit")[1].querySelector(".card") ?? null,
        "Seconds"
      ),
    }
    // 延迟注册到全局管理器，确保 DOM 已完全渲染
    setTimeout(() => {
      globalCountdownManager.register(this);
    }, 500);
  }

  /**
   * 组件从 DOM 中移除时调用
   */
  disconnectedCallback() {
    globalCountdownManager.unregister(this);
  }

  /**
   * 更新倒计时（由全局管理器调用）
   */
  update() {
    // 如果倒计时已结束，不再更新
    if (this.isCountdownFinished) {
      return;
    }

    // 更新倒计时
    const countdownDate = this.countdownDate;
    if (!countdownDate) {
      return;
    }
    let countdownTime = this.getTimeRemaining(countdownDate);
    if (countdownTime.Total < 0) {
      // 倒计时结束，设置所有数字为0
      for (let key in this.#countdownTrackers) {
        this.#countdownTrackers[key].update(0);
      }
      // 标记倒计时已结束
      this.isCountdownFinished = true;
      // 从管理器取消注册
      globalCountdownManager.unregister(this);
      return;
    }

    for (let key in this.#countdownTrackers) {
      this.#countdownTrackers[key].update(countdownTime[key]);
    }
  }
  getTime() {
    let t = new Date();
    return {
      Total: t,
      Hours: t.getHours() % 12,
      Minutes: t.getMinutes(),
      Seconds: t.getSeconds(),
    };
  }
  /**
   * 获取时间剩余
   * @param {any} endtime - 结束时间
   * @returns {any}
   */
  getTimeRemaining(endtime) {
    // 只在第一次调用时解析结束时间
    if (this.endTimeTimestamp === null) {
      this.endTimeTimestamp = Date.parse(endtime);
    }

    // 使用 performance.now() 获取当前时间，性能更好
    let currentTime = performance.now() + performance.timeOrigin;
    let t = this.endTimeTimestamp - currentTime;

    return {
      Total: t,
      Days: Math.floor(t / (1000 * 60 * 60 * 24)),
      Hours: Math.floor((t / (1000 * 60 * 60)) % 24),
      Minutes: Math.floor((t / 1000 / 60) % 60),
      Seconds: Math.floor((t / 1000) % 60),
    };
  }
}
if (!customElements.get("flip-countdown-component")) {
  customElements.define("flip-countdown-component", FlipCountdownComponent);
}

/**
 * 倒计时组件 - 简单的数字倒计时显示
 * 使用 requestAnimationFrame 进行高性能更新
 *
 * @extends {Component}
 */
class CountdownComponent extends Component {
  static get observedAttributes() {
    return ["countdown-date"];
  }

  requiredRefs = ["days", "hours", "minutes", "seconds"];

  /** @type {number | null} */
  #endTimeTimestamp = null;

  /** @type {boolean} */
  #isFinished = false;

  /** @type {IntersectionObserver | null} */
  #intersectionObserver = null;

  /** @type {boolean} */
  #isStarted = false;

  /**
   * 获取倒计时目标日期
   * @returns {Date | null}
   */
  get countdownDate() {
    const countdownDate = this.getAttribute("countdown-date");
    if (!countdownDate) {
      return null;
    }
    const date = new Date(countdownDate);
    // 检查日期是否有效
    if (isNaN(date.getTime())) {
      return null;
    }
    return date;
  }

  /**
   * 组件连接到 DOM 时调用
   */
  connectedCallback() {
    super.connectedCallback();

    // 检查是否使用 Intersection Observer
    const useObserver = this.dataset.useObserver === "true" || this.dataset.useObserver === "";

    if (useObserver) {
      if (!this.#intersectionObserver) {
        // 使用 observer，等待进入视口再启动
        this.#createIntersectionObserver();
      }
    } else {
      // 不使用 observer，立即启动倒计时
      this.#initCountdown();
    }
  }

  /**
   * 组件从 DOM 中移除时调用
   */
  disconnectedCallback() {
    globalCountdownManager.unregister(this);
    this.#destroyIntersectionObserver();
  }

  /**
   * 属性变化时调用
   * @param {string} name - 属性名
   * @param {string} oldValue - 旧值
   * @param {string} newValue - 新值
   */
  attributeChangedCallback(name, oldValue, newValue) {
    if (name === "countdown-date" && oldValue !== newValue) {
      // 先取消注册
      globalCountdownManager.unregister(this);
      this.#endTimeTimestamp = null;
      this.#isFinished = false;
      this.#isStarted = false;
      if (this.isConnected) {
        // 检查是否使用 observer
        const useObserver = this.dataset.useObserver === "true" || this.dataset.useObserver === "";
        if (useObserver) {
          // 如果使用 observer，重新创建 observer
          this.#createIntersectionObserver();
        } else {
          // 不使用 observer，立即启动
          this.#initCountdown();
        }
      }
    }
  }

  /**
   * 初始化倒计时
   */
  #initCountdown() {
    // 如果已经启动，不再重复启动
    if (this.#isStarted) {
      return;
    }

    const countdownDate = this.countdownDate;
    if (!countdownDate) {
      // 如果日期无效，显示 00
      this.#setTime(0, 0, 0, 0);
      return;
    }

    // 解析结束时间戳（只解析一次）
    if (this.#endTimeTimestamp === null) {
      this.#endTimeTimestamp = countdownDate.getTime();
    }

    // 重置状态
    this.#isFinished = false;
    this.#isStarted = true;

    // 注册到全局管理器
    globalCountdownManager.register(this);
  }

  /**
   * 更新倒计时（由全局管理器调用）
   */
  update() {
    // 如果倒计时已结束，不再更新
    if (this.#isFinished) {
      return;
    }

    // 计算剩余时间
    const timeRemaining = this.#getTimeRemaining();

    if (timeRemaining.total <= 0) {
      // 倒计时结束，设置所有数字为 00
      this.#setTime(0, 0, 0, 0);
      this.#isFinished = true;
      // 从管理器取消注册
      globalCountdownManager.unregister(this);
      return;
    }

    // 更新显示
    this.#setTime(
      timeRemaining.days,
      timeRemaining.hours,
      timeRemaining.minutes,
      timeRemaining.seconds
    );
  }

  /**
   * 获取剩余时间
   * @returns {{ total: number; days: number; hours: number; minutes: number; seconds: number }}
   */
  #getTimeRemaining() {
    if (this.#endTimeTimestamp === null) {
      return { total: 0, days: 0, hours: 0, minutes: 0, seconds: 0 };
    }

    // 使用 performance.now() 获取当前时间，性能更好
    const currentTime = performance.now() + performance.timeOrigin;
    const total = this.#endTimeTimestamp - currentTime;

    return {
      total,
      days: Math.floor(total / (1000 * 60 * 60 * 24)),
      hours: Math.floor((total / (1000 * 60 * 60)) % 24),
      minutes: Math.floor((total / 1000 / 60) % 60),
      seconds: Math.floor((total / 1000) % 60),
    };
  }

  /**
   * 设置倒计时显示
   * @param {number} days - 天数
   * @param {number} hours - 小时数
   * @param {number} minutes - 分钟数
   * @param {number} seconds - 秒数
   */
  #setTime(days, hours, minutes, seconds) {
    const daysEl = Array.isArray(this.refs.days) ? this.refs.days[0] : this.refs.days;
    const hoursEl = Array.isArray(this.refs.hours) ? this.refs.hours[0] : this.refs.hours;
    const minutesEl = Array.isArray(this.refs.minutes) ? this.refs.minutes[0] : this.refs.minutes;
    const secondsEl = Array.isArray(this.refs.seconds) ? this.refs.seconds[0] : this.refs.seconds;

    const daysStr = days.toString().padStart(2, "0");
    const hoursStr = hours.toString().padStart(2, "0");
    const minutesStr = minutes.toString().padStart(2, "0");
    const secondsStr = seconds.toString().padStart(2, "0");
    if (daysEl instanceof HTMLElement && daysEl.textContent !== daysStr) {
      daysEl.textContent = daysStr;
    }
    if (hoursEl instanceof HTMLElement && hoursEl.textContent !== hoursStr) {
      hoursEl.textContent = hoursStr;
    }
    if (minutesEl instanceof HTMLElement && minutesEl.textContent !== minutesStr) {
      minutesEl.textContent = minutesStr;
    }
    if (secondsEl instanceof HTMLElement && secondsEl.textContent !== secondsStr) {
      secondsEl.textContent = secondsStr;
    }
  }


  /**
   * 创建 Intersection Observer
   */
  #createIntersectionObserver() {
    // 如果浏览器不支持 Intersection Observer，立即启动倒计时
    if (typeof IntersectionObserver === "undefined") {
      this.#initCountdown();
      return;
    }

    // 如果已经存在 observer，先销毁
    this.#destroyIntersectionObserver();

    // 创建 Intersection Observer
    this.#intersectionObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          // 当组件进入视口时，启动倒计时
          if (entry.isIntersecting && !this.#isStarted) {
            this.#initCountdown();
            // 启动后可以断开 observer（可选，如果只需要启动一次）
            // this.#destroyIntersectionObserver();
          }
        });
      },
      {
        // 当组件进入视口 10% 时触发
        threshold: 0.1,
        // 在视口外 50px 时提前触发
        rootMargin: "50px",
      }
    );

    // 开始观察
    this.#intersectionObserver.observe(this);
  }

  /**
   * 销毁 Intersection Observer
   */
  #destroyIntersectionObserver() {
    if (this.#intersectionObserver) {
      this.#intersectionObserver.disconnect();
      this.#intersectionObserver = null;
    }
  }
}

// 注册自定义元素
if (!customElements.get("countdown-component")) {
  customElements.define("countdown-component", CountdownComponent);
}

class LinkComponent extends Component {
  /** @type {number} */
  touchStartX = 0;
  
  /** @type {number} */
  touchStartY = 0;
  
  /** @type {boolean} */
  hasMoved = false;

  connectedCallback() {
    super.connectedCallback();
    const url = this.getAttribute("data-url");
    if (url) {
      this.style.cursor = "pointer";
    }
    
    // 监听鼠标按下事件（桌面端）
    this.addEventListener('mousedown', (e) => {
      this.touchStartX = e.clientX;
      this.touchStartY = e.clientY;
      this.hasMoved = false;
    });
    
    // 监听鼠标移动事件（桌面端）
    this.addEventListener('mousemove', (e) => {
      if (!this.hasMoved) {
        const deltaX = Math.abs(e.clientX - this.touchStartX);
        const deltaY = Math.abs(e.clientY - this.touchStartY);
        // 如果移动距离超过 5px，认为是拖动操作
        if (deltaX > 5 || deltaY > 5) {
          this.hasMoved = true;
        }
      }
    });
    
    this.addEventListener('mouseup', () => {
      setTimeout(() => {
        this.hasMoved = false;
      }, 100);
    });
  }
  
  handleClick(/** @type {Event} */ e) {
    // 如果检测到滑动/拖动操作，不执行跳转
    if (this.hasMoved) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    
    e.preventDefault();
    e.stopPropagation();
    const url = this.getAttribute("data-url");
    if (url) {
      window.location.href = url;
    }
  }
}
if (!customElements.get('link-component')) {
  customElements.define('link-component', LinkComponent);
}

class VariantSwitcher extends Component {
  requiredRefs = ['imgEl']

  /** @type {HTMLElement[]} */
  colorBars = []
  connectedCallback() {
    super.connectedCallback()
    this.colorBars = Array.from(this.querySelectorAll('.color-bar'))
  }
  handleColorChange(/** @type {MouseEvent} */ event) {
    if (!(event.target instanceof HTMLElement)) {
      return
    }
    const dataImg = event.target.getAttribute('data-img')
    const varinatId = event.target.getAttribute('data-var')
    if (dataImg && varinatId) {
      if (this.refs.imgEl instanceof HTMLImageElement) {
        this.refs.imgEl.src = dataImg
        this.colorBars.forEach(bar => bar.classList.remove('active'))
        event.target.classList.add('active')
        const idInputs = this.querySelectorAll('form [name="id"]')
        idInputs.forEach(input => {
          if (input instanceof HTMLInputElement) {
            input.value = varinatId
          }
        })
      }
    }
  }
}
if (!customElements.get('variant-switcher')) {
  customElements.define('variant-switcher', VariantSwitcher)
}